import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  BusinessEntityType,
  BuyerAddressView,
  CaseCreatedResponse,
  CaseListResponse,
  CaseStage,
  CaseView,
  EquipmentCondition,
  EquipmentTitleKind,
  MoneyView,
  SoftCostKind,
} from '@tex/finance-shared'
import { ApiError, apiGet, apiSend } from './api'

const base = '/api/v1/cases'
const DEALER_SCOPE_HEADER = 'X-Dealer-Id'
const IDEMPOTENCY_HEADER = 'Idempotency-Key'

/** Request bodies mirror the zod schemas in apps/api/src/routes/cases.ts. */
export interface BuyerInput {
  business_name: string
  contact_name: string
  contact_email: string
  contact_phone?: string | null
  state?: string | null
  equipment_use_state?: string | null
  entity_type?: BusinessEntityType | null
  address?: BuyerAddressView | null
}

export interface EquipmentLineInput {
  category: string
  make?: string | null
  model?: string | null
  model_year?: number | null
  condition: EquipmentCondition
  quantity: number
  unit_price: MoneyView
  serial_number?: string | null
  hours_or_miles?: number | null
  listing_url?: string | null
  title_kind?: EquipmentTitleKind | null
  vehicle_class?: string | null
  existing_lien_noted: boolean
  description?: string | null
}

export interface SoftCostInput {
  kind: SoftCostKind
  label?: string | null
  amount: MoneyView
}

export interface PricingInput {
  soft_costs?: SoftCostInput[]
  discount?: MoneyView
  cash_contribution?: MoneyView
  requested_principal?: MoneyView
  reconciliation_reason?: string | null
  trade_in_noted: boolean
}

export interface DeliveryInput {
  expected_on?: string | null
  pre_delivery_payment_requested?: boolean
}

export interface CreateCaseInput {
  buyer: BuyerInput
  equipment: EquipmentLineInput[]
  pricing?: PricingInput
  delivery?: DeliveryInput
  dealer_note?: string | null
  /** Issue the borrower share link in the same transaction as the case. */
  invite?: { send: true; email?: string; display_name?: string | null }
}

export interface LeadPatchInput {
  expected_version: number
  buyer?: Partial<BuyerInput>
  dealer_note?: string | null
  delivery?: DeliveryInput
}

export interface CaseListFilters {
  stage?: CaseStage
  q?: string
  /** Which of the caller's dealer memberships to act for; ignored by the API for Tex staff. */
  dealerId?: string
}

export const caseKeys = {
  list: (f: CaseListFilters) => ['cases', 'list', f.dealerId ?? 'all', f.stage ?? 'all', f.q ?? ''] as const,
  detail: (id: string) => ['cases', id] as const,
}

function scopeHeaders(dealerId?: string): Record<string, string> {
  return dealerId ? { [DEALER_SCOPE_HEADER]: dealerId } : {}
}

export function useCases(filters: CaseListFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: caseKeys.list(filters),
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams()
      if (filters.stage) qs.set('stage', filters.stage)
      if (filters.q) qs.set('q', filters.q)
      if (pageParam) qs.set('cursor', pageParam)
      const query = qs.toString()
      return apiGet<CaseListResponse>(`${base}${query ? `?${query}` : ''}`, scopeHeaders(filters.dealerId))
    },
    getNextPageParam: (last) => last.next_cursor,
  })
}

export function useCase(id: string) {
  return useQuery({ queryKey: caseKeys.detail(id), queryFn: () => apiGet<CaseView>(`${base}/${id}`) })
}

/**
 * `idempotencyKey` is minted once per form and reused on retry, so a double submit or a
 * network blip after the server committed replays the same case instead of creating another.
 */
export function useCreateCase(dealerId: string, idempotencyKey: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateCaseInput) =>
      apiSend<CaseCreatedResponse>('POST', base, input, { ...scopeHeaders(dealerId), [IDEMPOTENCY_HEADER]: idempotencyKey }),
    onSuccess: ({ case: c }) => {
      qc.setQueryData(caseKeys.detail(c.id), c)
      return qc.invalidateQueries({ queryKey: ['cases', 'list'] })
    },
  })
}

function useCaseMutation<TVars>(caseId: string, fn: (vars: TVars) => Promise<CaseView>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (view) => {
      qc.setQueryData(caseKeys.detail(caseId), view)
      return qc.invalidateQueries({ queryKey: ['cases', 'list'] })
    },
    // A conflict means our cached `version` is stale; refetch so the next attempt carries the current one.
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'version_conflict') {
        return qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) })
      }
    },
  })
}

export function useUpdateLead(caseId: string) {
  return useCaseMutation(caseId, (patch: LeadPatchInput) => apiSend<CaseView>('PATCH', `${base}/${caseId}/lead`, patch))
}

export function useReplaceEquipment(caseId: string) {
  return useCaseMutation(caseId, (body: { expected_version: number; equipment: EquipmentLineInput[] }) =>
    apiSend<CaseView>('PUT', `${base}/${caseId}/equipment`, body),
  )
}

export function useUpdatePricing(caseId: string) {
  return useCaseMutation(caseId, (body: { expected_version: number; pricing: PricingInput }) =>
    apiSend<CaseView>('PUT', `${base}/${caseId}/pricing`, body),
  )
}

export function useWithdrawCase(caseId: string) {
  return useCaseMutation(caseId, (body: { expected_version: number; reason: string }) =>
    apiSend<CaseView>('POST', `${base}/${caseId}/withdraw`, body),
  )
}
